import { useState } from "react";
import { Table, Button, Modal, Form, Input, InputNumber, DatePicker, Popconfirm, message, Tag, Divider, Select } from "antd";
import { Plus } from "lucide-react";
import dayjs from "dayjs";
import { useHRUsers, useNMWRates, useNMWRateCreate, useNMWChecks, useNMWCheckCreate, useNMWCheckDelete } from "../../../../api-hooks/hr";

export default function NMWTab({ cid }: { cid: number }) {
  const [msgApi, ctx] = message.useMessage();
  const [rateForm] = Form.useForm();
  const [checkForm] = Form.useForm();
  const [rateModal, setRateModal] = useState(false);
  const [checkModal, setCheckModal] = useState(false);

  const { data: users = [] } = useHRUsers(cid);
  const { data: rates = [], isLoading: rLoading } = useNMWRates();
  const { data: checks = [], isLoading: cLoading } = useNMWChecks(cid);

  const cb = (msg: string, fn?: () => void) => ({ onSuccess: () => { msgApi.success(msg); fn?.(); }, onError: (e: any) => msgApi.error(e?.message || "Error") });
  const rateCreate = useNMWRateCreate(cb("Rate added.", () => setRateModal(false)));
  const checkCreate = useNMWCheckCreate(cid, cb("Check added.", () => setCheckModal(false)));
  const checkDel = useNMWCheckDelete(cid, { onSuccess: () => msgApi.success("Deleted."), onError: (e: any) => msgApi.error(e?.message) });

  const rateCols = [
    { title: "Age Bracket", dataIndex: "age_bracket", key: "ab" },
    { title: "Effective From", dataIndex: "effective_from", key: "ef", render: (v: string) => v ? dayjs(v).format("DD/MM/YYYY") : "—" },
    { title: "Hourly Rate", dataIndex: "hourly_rate", key: "hr", render: (v: number) => `£${v}` },
  ];

  const checkCols = [
    { title: "Employee", dataIndex: "user_name", key: "u" },
    { title: "Check Date", dataIndex: "check_date", key: "cd", render: (v: string) => v ? dayjs(v).format("DD/MM/YY") : "—" },
    { title: "Effective Rate", dataIndex: "effective_rate", key: "er", render: (v: number) => `£${v}` },
    { title: "NMW Rate", dataIndex: "nmw_rate", key: "nr", render: (v: number) => `£${v}` },
    { title: "Compliant", dataIndex: "is_compliant", key: "ic", render: (v: number) => <Tag color={v ? "green" : "red"}>{v ? "Yes" : "No"}</Tag> },
    { title: "Shortfall", dataIndex: "shortfall", key: "sf", render: (v: number) => v ? <span className="text-red-600">£{v}</span> : "—" },
    { title: "", key: "act", render: (_: any, r: any) => <Popconfirm title="Delete?" onConfirm={() => checkDel.mutate({ id: r.id })} okButtonProps={{ danger: true }}><Button size="small" danger>Del</Button></Popconfirm> },
  ];

  return (
    <div>
      {ctx}
      {/* NMW Rates */}
      <div className="flex justify-between mb-2">
        <h3 className="font-semibold text-slate-700 text-sm">NMW / NLW Rates (Global Reference)</h3>
        <Button size="small" type="primary" icon={<Plus size={13} />} onClick={() => { setRateModal(true); rateForm.resetFields(); }} style={{ background: "#4c1d95" }}>Add Rate</Button>
      </div>
      <div className="bg-slate-50 rounded-lg border border-slate-100 mb-5">
        <Table rowKey="id" dataSource={rates as any[]} columns={rateCols} loading={rLoading} size="small" pagination={false} />
      </div>

      <Divider plain className="my-3">Compliance Checks</Divider>
      <div className="flex justify-end mb-2">
        <Button size="small" type="primary" icon={<Plus size={13} />} onClick={() => { setCheckModal(true); checkForm.resetFields(); }} style={{ background: "#4c1d95" }}>Add Check</Button>
      </div>
      <Table rowKey="id" dataSource={checks as any[]} columns={checkCols} loading={cLoading} size="small" pagination={false} />

      {/* Rate Modal */}
      <Modal open={rateModal} onCancel={() => setRateModal(false)} footer={null} destroyOnClose title="New NMW Rate" width={380}>
        <Form form={rateForm} layout="vertical" size="middle" className="pt-2" onFinish={(vals) => rateCreate.mutate({ ...vals, effective_from: vals.effective_from?.format("YYYY-MM-DD") })}>
          <Form.Item name="age_bracket" label="Age Bracket" rules={[{ required: true }]}><Input placeholder="e.g. 21+" /></Form.Item>
          <Form.Item name="effective_from" label="Effective From" rules={[{ required: true }]}><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
          <Form.Item name="hourly_rate" label="Hourly Rate (£)" rules={[{ required: true }]}><InputNumber className="w-full" min={0} step={0.01} /></Form.Item>
          <div className="flex gap-2 pt-1">
            <Button type="primary" htmlType="submit" style={{ background: "#4c1d95" }} loading={rateCreate.isPending}>Save</Button>
            <Button onClick={() => setRateModal(false)}>Cancel</Button>
          </div>
        </Form>
      </Modal>

      {/* Check Modal */}
      <Modal open={checkModal} onCancel={() => setCheckModal(false)} footer={null} destroyOnClose title="NMW Compliance Check" width={440}>
        <Form form={checkForm} layout="vertical" size="middle" className="pt-2" onFinish={(vals) => checkCreate.mutate({ ...vals, check_date: vals.check_date?.format("YYYY-MM-DD"), is_compliant: vals.effective_rate >= vals.nmw_rate ? 1 : 0, shortfall: vals.effective_rate < vals.nmw_rate ? (vals.nmw_rate - vals.effective_rate).toFixed(4) : null })}>
          <Form.Item name="user_id" label="Employee" rules={[{ required: true }]}><Select options={(users as any[])?.map((u: any) => ({ value: u.id, label: u.name }))} showSearch optionFilterProp="label" /></Form.Item>
          <Form.Item name="check_date" label="Check Date" rules={[{ required: true }]}><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
          <div className="grid grid-cols-2 gap-x-3">
            <Form.Item name="effective_rate" label="Effective Rate (£)" rules={[{ required: true }]}><InputNumber className="w-full" min={0} step={0.01} /></Form.Item>
            <Form.Item name="nmw_rate" label="Applicable NMW (£)" rules={[{ required: true }]}><InputNumber className="w-full" min={0} step={0.01} /></Form.Item>
          </div>
          <Form.Item name="notes" label="Notes"><Input.TextArea rows={2} /></Form.Item>
          <div className="flex gap-2 pt-1">
            <Button type="primary" htmlType="submit" style={{ background: "#4c1d95" }} loading={checkCreate.isPending}>Save</Button>
            <Button onClick={() => setCheckModal(false)}>Cancel</Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}

